import React from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, GiftOutlined, LockOutlined, TagOutlined } from '@ant-design/icons';
import type { FormattedPrice } from '../../utils/format';
import type { CartPricing } from '../../hooks/useCartPricing';
import { cartStyles as s } from './cart.styles';

interface CartSummaryProps {
  pricing: CartPricing;
  /** Formateador según el modo de precios del partner (ver usePrice). */
  price: (amount: number) => FormattedPrice;
  isStoreOpen: boolean;
  /** false = el partner no tiene tasa activa y el backend rechazaría la orden. */
  canOrder: boolean;
  onCheckout: () => void;
}

/** Totales y CTA del carrito. No conoce Redux: recibe todo por props. */
export const CartSummary: React.FC<CartSummaryProps> = ({
  pricing,
  price,
  isStoreOpen,
  canOrder,
  onCheckout,
}) => {
  if (!isStoreOpen) {
    return (
      <div style={s.closedNotice} role="status">
        <LockOutlined style={{ fontSize: 16, marginTop: 2, flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Sucursal cerrada</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>Pedidos no disponibles ahora.</div>
        </div>
      </div>
    );
  }

  if (!canOrder) {
    return (
      <div style={s.closedNotice} role="status">
        <LockOutlined style={{ fontSize: 16, marginTop: 2, flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Pedidos en pausa</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>La tienda no tiene tasa activa.</div>
        </div>
      </div>
    );
  }

  const payable = pricing.hasDiscount ? pricing.discountedSubtotal : pricing.subtotal;
  const total = price(payable);

  return (
    <div>
      {pricing.hasDiscount && (
        <>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>Subtotal</span>
            <span style={{ ...s.summaryValue, textDecoration: 'line-through' }}>
              {price(pricing.subtotal).primary}
            </span>
          </div>
          {pricing.totalSaving > 0 && (
            <div style={s.savingRow}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TagOutlined style={{ fontSize: 12 }} /> Ahorro por ofertas
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>
                -{price(pricing.totalSaving).primary}
              </span>
            </div>
          )}
          {pricing.comboSaving > 0 && (
            <div style={s.savingRow}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <GiftOutlined style={{ fontSize: 12 }} /> Ahorro por promos
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>
                -{price(pricing.comboSaving).primary}
              </span>
            </div>
          )}
        </>
      )}

      <div style={{ ...s.summaryRow, marginBottom: total.secondary ? 2 : 14 }}>
        <span style={s.totalLabel}>{pricing.hasDiscount ? 'Total con oferta' : 'Subtotal'}</span>
        <span
          key={total.primary}
          className="cart-tick"
          style={{
            ...s.totalValue,
            color: pricing.hasDiscount ? 'var(--color-accent)' : 'var(--color-ink)',
          }}
        >
          {total.primary}
        </span>
      </div>

      {total.secondary && (
        <div style={{ ...s.summaryRow, justifyContent: 'flex-end', marginBottom: 14 }}>
          <span style={s.vesValue}>{total.secondary}</span>
        </div>
      )}

      <Button
        type="primary"
        block
        size="large"
        icon={<ArrowRightOutlined />}
        onClick={onCheckout}
        style={s.checkoutButton}
      >
        Ir a pagar
      </Button>
    </div>
  );
};
